import { List,Map } from "immutable";

import { MINDMAP_IMPORT_SPACING } from "../../constants";
import { createFromJS } from "../../types";
import combineReducersWithGlobal from "../combineReducersWithGlobal";
import { itemActionTypes } from "../items";
import { actionTypes } from "./actions";

const nodeIdsForItemIds = (mindmap, itemIds) => {
  return mindmap.nodes.byId
    .filter(n => itemIds.indexOf(n.itemId) >= 0)
    .map(n => n.id)
    .toList()
    .toArray();
};

const createNode = (id, itemId, left, top) => {
  return createFromJS.MindmapNodeRecord({ id, itemId, left, top });
};

const moveNode = (node, action) => {
  const obj = {
    left: node.get("left") + action.deltaX,
    top: node.get("top") + action.deltaY
  };
  return node.merge(obj);
};

// moveNodes only affects the "byIds" group since the list of ids does not change.
const moveNodes = (mindmap, action) => {
  return mindmap.updateIn(["nodes", "byId"], byIds =>
    byIds.map(n => {
      if (action.nodeIds.indexOf(n.get("id")) !== -1) {
        return moveNode(n, action);
      }
      return n;
    })
  );
};

// importNodes affects byId and allIds.
const createNodes = (mindmap, action) => {
  let x = action.x;
  let y = action.y;

  // Create new list of nodes
  const newNodes = {};
  action.items.forEach(o => {
    newNodes[o.nodeId] = createNode(o.nodeId, o.itemId, x, y);
    x += MINDMAP_IMPORT_SPACING;
    y += MINDMAP_IMPORT_SPACING;
  });

  // Create list of new IDs
  const newNodeIds = action.items.map(o => o.nodeId);

  // Finally, update the mindmap
  return mindmap.withMutations(m => {
    m.updateIn(["nodes", "allIds"], allIds => allIds.concat(newNodeIds));
    m.updateIn(["nodes", "byId"], byId => byId.merge(newNodes));
  });
};

const deleteNodesForItems = (mindmap, action) => {
  const nodeIds = nodeIdsForItemIds(mindmap, action.ids);
  return mindmap.withMutations(m => {
    // remove the node from
    nodeIds.forEach(nId => {
      m.deleteIn(["nodes", "byId", nId]);
    });
    // remove the nodeIds out of the allIds list.
    m.updateIn(["nodes", "allIds"], allIds => allIds.filter(n => nodeIds.indexOf(n) < 0));
  });
};

const toggleShowReferences = (mindmap, action) => {
  if (action.showReference) {
    return mindmap.update("hiddenReferenceTypes", refs => refs.filter(rId => rId !== action.referenceId));
  } else {
    return mindmap.update("hiddenReferenceTypes", refs =>
      refs.contains(action.referenceId) ? refs : refs.push(action.referenceId)
    );
  }
};

const updateMindmap = (state, action, _globalState) => {
  // state here is the entire byId group of a map...
  return state.map(m => {
    // handle deleting items - needs to clear out of all mindmaps at once
    if (action.type === itemActionTypes.DELETE_ITEMS) {
      return deleteNodesForItems(m, action);
    }
    if (m.get("id") === action.mindmapId) {
      switch (action.type) {
        case actionTypes.CREATE_NODE:
        case actionTypes.IMPORT_NODES:
          return createNodes(m, action);
        case actionTypes.DELETE_NODES_FOR_ITEMS:
          return deleteNodesForItems(m, action);
        case actionTypes.MOVE_NODES:
          return moveNodes(m, action);
        case actionTypes.TOGGLE_SHOW_REFERENCE_CONNECTION:
          return toggleShowReferences(m, action);
        default:
          return m;
      }
    } else {
      return m;
    }
  });
};

const createMindmap = (mindmapId, existingNames) => {
  let chosenName = "Mindmap 1";
  let count = 1;
  while (existingNames.indexOf(chosenName) >= 0) {
    count++;
    chosenName = `Mindmap ${count}`;
  }

  return createFromJS.MindmapRecord({
    id: mindmapId,
    label: chosenName,
    nodes: {
      byId: {},
      allIds: []
    },
    hiddenReferenceTypes: []
  });
};

const byId = (state = Map(), action, globalState) => {
  switch (action.type) {
    // Changing mindmaps
    case actionTypes.CREATE_MINDMAP:
      return state.set(
        action.mindmapId,
        createMindmap(
          action.mindmapId,
          state.toList().map(m => m.get("label"))
        )
      );
    case actionTypes.RENAME_MINDMAP:
      return state.setIn([action.mindmapId, "label"], action.label);
    case actionTypes.DELETE_MINDMAP:
      return state.delete(action.mindmapId);

    // Changing nodes within mindmaps
    case actionTypes.CREATE_NODE:
    case actionTypes.IMPORT_NODES:
    case actionTypes.DELETE_NODES_FOR_ITEMS:
    case actionTypes.MOVE_NODES:
    case actionTypes.TOGGLE_SHOW_REFERENCE_CONNECTION:
    case itemActionTypes.DELETE_ITEMS:
      return updateMindmap(state, action, globalState);
    default:
      return state;
  }
};

const allIds = (state = List(), action, _globalState) => {
  switch (action.type) {
    case actionTypes.CREATE_MINDMAP:
      return state.push(action.mindmapId);
    case actionTypes.DELETE_MINDMAP:
      return state.filter(m => m !== action.mindmapId);
    default:
      return state;
  }
};

export default combineReducersWithGlobal(createFromJS.IdReferenceList("MindmapRecord", {}), {
  byId,
  allIds
});

// export default reducer;
